import {
  ScheduledEventDto,
  ScheduledEventFormSchema,
  ScheduledEventFormType,
  SuggestedScheduledEventDto,
  UserDto,
} from '@act/shared/data-transfer-objects';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { scheduledEventRequests } from 'redux/requests/scheduled-event.requests';
import {
  createScheduledEventSuccess,
  updateScheduledEventSuccess,
} from 'redux/slices/scheduledEventSlice.slice';
import { useAppDispatch } from 'redux/storeHooks';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { useToast } from '../../app/ReusableToast';
import { awaitFlushPendingNoteAutosave } from '../flushPendingNoteAutosave.util';
import { handleNoteFieldClose } from '../noteFieldClose.util';
import useUnsavedChangesDialog from './useUnsavedChangesDialog.hook';
import { useScheduledEventFormConfig } from './useScheduledEventFormConfig.hook';
import { NoteFieldRef } from '../NoteField';
import {
  buildScheduledEventSaveBody,
  mapScheduledEventToFormDefaults,
} from '../scheduledEventDialog.util';

export type UseScheduledEventFormProps = {
  user: UserDto;
  scheduledEvent?: ScheduledEventDto;
  suggestedEvent?: SuggestedScheduledEventDto;
  /** When false, the shell is hidden; when true, form values sync to prefill defaults. */
  open: boolean;
  onClose: (success?: boolean) => void;
  /** When true, includes tracking names on form fields (dialog flow). */
  includeTrackingNames?: boolean;
};

/**
 * Shared form state, effects, and persistence for scheduled-event create/edit flows.
 * @param props - Member, optional prefill sources, open state, and close callback.
 * @returns Form API, config, and handlers for dialog or side-panel shells.
 */
export function useScheduledEventForm({
  user,
  scheduledEvent,
  suggestedEvent,
  open,
  onClose,
  includeTrackingNames = false,
}: UseScheduledEventFormProps) {
  const isEditMode = Boolean(scheduledEvent?.id);
  const prefillKey =
    scheduledEvent?.id ??
    suggestedEvent?.id ??
    (suggestedEvent ? `suggested-call-${suggestedEvent.callId}` : 'create');
  const defaultsSource = useMemo(
    () => ({ scheduledEvent, suggestedEvent }),
    [scheduledEvent, suggestedEvent]
  );

  const formDefaults = useMemo(
    () => mapScheduledEventToFormDefaults(defaultsSource),
    [defaultsSource]
  );

  const { control, handleSubmit, setValue, formState, reset } =
    useForm<ScheduledEventFormType>({
      defaultValues: formDefaults,
      mode: suggestedEvent ? 'onChange' : undefined,
      resolver: zodResolver(ScheduledEventFormSchema),
    });

  const dispatch = useAppDispatch();
  const toast = useToast();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const noteFieldRef = useRef<NoteFieldRef>(null);

  const { formConfig, showRecoveredNote } = useScheduledEventFormConfig({
    control,
    setValue,
    user,
    scheduledEvent,
    suggestedEvent,
    isEditMode,
    noteFieldRef,
    includeTrackingNames,
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    reset(formDefaults);
  }, [open, prefillKey, formDefaults, reset]);

  const [
    createdScheduledEvent,
    isCreatingScheduledEvent,
    createScheduledEvent,
  ] = useApiCall(scheduledEventRequests.createScheduledEventForUser);
  const [
    updatedScheduledEvent,
    isUpdatingScheduledEvent,
    updateScheduledEvent,
  ] = useApiCall(scheduledEventRequests.updateScheduledEvent);

  async function handleSave(data: ScheduledEventFormType) {
    if (isEditMode) {
      await awaitFlushPendingNoteAutosave(noteFieldRef);
    }

    const body = buildScheduledEventSaveBody({
      form: data,
      suggestedEventId: suggestedEvent?.id,
    });

    try {
      if (isEditMode && scheduledEvent) {
        updateScheduledEvent({
          params: { userId: user.id, scheduledEventId: scheduledEvent.id },
          body,
        });
      } else {
        createScheduledEvent({
          params: { userId: user.id },
          body,
        });
      }
    } catch (err) {
      console.error(`Error saving scheduled event: ${err}`);
    }
  }

  useEffect(() => {
    if (createdScheduledEvent) {
      dispatch(createScheduledEventSuccess(createdScheduledEvent));
      noteFieldRef.current?.clearDraft();
      toast('Scheduled event has been created successfully');
      onClose(true);
    }
  }, [createdScheduledEvent, dispatch, onClose, toast]);

  useEffect(() => {
    if (updatedScheduledEvent && !isUpdatingScheduledEvent) {
      dispatch(updateScheduledEventSuccess(updatedScheduledEvent));
      toast('Scheduled event has been updated successfully');
      onClose(true);
    }
  }, [
    updatedScheduledEvent,
    isUpdatingScheduledEvent,
    dispatch,
    onClose,
    toast,
  ]);

  const isSaving = isCreatingScheduledEvent || isUpdatingScheduledEvent;
  const isSubmitting = isSaving || formState.isSubmitting;
  const { isDirty } = formState;

  const handleClose = () => {
    handleNoteFieldClose({
      mode: isEditMode ? 'edit' : 'create',
      isDirty,
      noteFieldRef,
      handleUnsavedChanges,
      finishClose: () => {
        reset();
        onClose();
      },
    });
  };

  return {
    control,
    formState,
    handleSubmit,
    handleSave,
    handleClose,
    formConfig,
    isSaving,
    isSubmitting,
    showRecoveredNote,
    isEditMode,
  };
}
